<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useOrderPayments } from "@/composables";
import { useValidation } from "@/composables/useValidation";
import type { OrderStatus } from "@/constants/order";
import {
  NON_SELECTABLE_ORDER_PAYMENT_TYPES,
  ORDER_PAYMENT_ACCESSORIAL_TYPES,
  ORDER_PAYMENT_DEDUCTION_TYPES,
} from "@/constants/order";
import { preventMinusKey, preventMinusPaste } from "@/utils/input";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import type { OrderPayment } from "@/api/orders/dto/order.dto";

const payment = defineModel<OrderPayment>({ required: true });

const props = defineProps<{
  orderStatus?: OrderStatus;
}>();

const emit = defineEmits<{
  delete: [];
}>();

const { t } = useI18n();
const { requiredValidator } = useValidation();
const { getMandatoryOrderPaymentType, isDeductionPaymentType } = useOrderPayments();

const isMandatory = computed(() => {
  return payment.value.type === getMandatoryOrderPaymentType(props.orderStatus);
});

const isNonSelectableType = computed(() => {
  return payment.value.type !== null && NON_SELECTABLE_ORDER_PAYMENT_TYPES.includes(payment.value.type);
});

const isPaymentDeduction = computed(() => isDeductionPaymentType(payment.value.type));

const typeOptions = computed(() => {
  if (isNonSelectableType.value) {
    return [{ id: payment.value.type, name: t(`order-payments.${payment.value.type}`) }];
  }

  return [
    { header: t("order-payments.accessorials") },
    ...ORDER_PAYMENT_ACCESSORIAL_TYPES.map((type) => ({ id: type, name: t(`order-payments.${type}`) })),
    { header: t("order-payments.deductions") },
    ...ORDER_PAYMENT_DEDUCTION_TYPES.map((type) => ({ id: type, name: t(`order-payments.${type}`) })),
  ];
});

const typeRules = computed(() => {
  if (isMandatory.value) {
    return [requiredValidator];
  }

  return [(value: unknown) => requiredValidator(value) === true || t("order-payments.type-required")];
});

const rateRules = computed(() => {
  if (isMandatory.value) {
    return [requiredValidator];
  }

  return [(value: unknown) => requiredValidator(value) === true || t("order-payments.rate-required")];
});

const displayedRate = computed(() => {
  if (payment.value.rate === null) {
    return "";
  }

  return isPaymentDeduction.value ? Math.abs(payment.value.rate) : payment.value.rate;
});

const paymentPrefix = computed(() => {
  return isPaymentDeduction.value ? "-" : undefined;
});

function updatePayment(data: Partial<OrderPayment>) {
  payment.value = {
    ...payment.value,
    ...data,
  };
}

function updateType(type: string | null) {
  if (payment.value.rate === null) {
    updatePayment({ type });
    return;
  }

  const rate = Math.abs(payment.value.rate);

  updatePayment({ type, rate: isDeductionPaymentType(type) ? -rate : rate });
}

function updateRate(value: string) {
  if (value === "") {
    updatePayment({ rate: null });
    return;
  }

  const rate = Math.abs(Number(value));

  updatePayment({ rate: isPaymentDeduction.value ? -rate : rate });
}
</script>

<template>
  <div class="d-flex align-center col-gap-4" data-qa="order-payment-row">
    <omni-select
      :disabled="payment.isDeleted"
      :items="typeOptions"
      :label="t('general.type')"
      :model-value="payment.type"
      :readonly="isNonSelectableType"
      :required="!isNonSelectableType"
      :rules="typeRules"
      class="flex-grow-1"
      data-qa="order-payment-type"
      @update:model-value="updateType"
    />

    <v-text-field
      :disabled="payment.isDeleted"
      :label="t('general.rate')"
      :model-value="displayedRate"
      :prefix="paymentPrefix"
      :rules="rateRules"
      autocomplete="off"
      class="required order-payment__rate"
      color="primary"
      data-qa="order-payment-rate"
      density="compact"
      hide-details="auto"
      min="0"
      type="number"
      variant="outlined"
      @keydown="preventMinusKey"
      @paste="preventMinusPaste"
      @update:model-value="updateRate"
    >
      <template #append-inner>
        <span class="font-size-16 text-grey-darken-1">$</span>
      </template>
    </v-text-field>

    <div class="order-payment__delete-box">
      <v-btn
        v-if="!isMandatory"
        :disabled="payment.isDeleted"
        color="danger"
        data-qa="order-payment-delete"
        icon="mdi-delete-outline"
        size="small"
        variant="text"
        @click="emit('delete')"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.order-payment {
  &__rate {
    flex: 0 0 35%;

    &:deep(.v-text-field__prefix) {
      transform: translateX(4px);
    }
  }

  &__delete-box {
    width: 40px;
    height: 40px;
  }
}
</style>
